import jsPDF from 'jspdf'

const INCIDENT_TYPE_LABELS = {
    motor_vehicle: 'Motor vehicle accident',
    construction: 'Construction accident',
    other_workplace: 'Other workplace accident',
    slip_trip_fall: 'Slip, trip, or fall',
    pedestrian_bicycle: 'Pedestrian or bicycle accident',
    assault: 'Assault',
    sports: 'Sports / recreational injury',
    other: 'Other',
}

const DATE_CERTAINTY_LABELS = {
    exact: 'Exact',
    approximate: 'Approximate',
    unknown: 'Unknown',
}

const COMPLETION_METHOD_LABELS = {
    online: 'Patient self',
    phone_interview: 'Phone interview',
}

const INFORMATION_PROVIDER_LABELS = {
    patient: 'Patient',
    parent: 'Parent / legal guardian',
    caregiver: 'Caregiver',
    other: 'Other',
}

const MEDICAL_CONDITION_LABELS = {
    none_reported: 'None reported',
    has_conditions: 'Has conditions',
    unknown: 'Unknown',
}

const MEDICATION_LABELS = {
    yes: 'Yes',
    no: 'No',
    unsure: 'Unknown',
}

const ALLERGY_LABELS = {
    no_known_drug_allergies: 'No known drug allergies',
    yes: 'Yes',
    unknown: 'Unknown',
}

const SMOKING_LABELS = {
    never: 'Never',
    former: 'Former',
    current: 'Current',
    unknown: 'Unknown',
}

const ALCOHOL_LABELS = {
    none: 'None',
    occasional: 'Occasional',
    regular: 'Regular',
    former: 'Former',
    declined: 'Declined',
}

const DRUG_USE_LABELS = {
    no: 'No',
    yes: 'Yes',
    former: 'Former',
    declined: 'Declined',
}

const FAMILY_HISTORY_LABELS = {
    none_known: 'None known',
    has_history: 'Has history',
    unknown: 'Unknown',
}

const BODY_REGION_LABELS = {
    head: 'Head',
    neck: 'Neck',
    upper_back: 'Upper back',
    mid_back: 'Mid back',
    low_back: 'Low back',
    right_shoulder: 'Right shoulder',
    left_shoulder: 'Left shoulder',
    right_arm: 'Right arm',
    left_arm: 'Left arm',
    right_elbow: 'Right elbow',
    left_elbow: 'Left elbow',
    right_wrist_hand: 'Right wrist/hand',
    left_wrist_hand: 'Left wrist/hand',
    chest: 'Chest',
    abdomen: 'Abdomen',
    right_hip: 'Right hip',
    left_hip: 'Left hip',
    right_thigh: 'Right thigh',
    left_thigh: 'Left thigh',
    right_knee: 'Right knee',
    left_knee: 'Left knee',
    right_leg: 'Right leg',
    left_leg: 'Left leg',
    right_ankle_foot: 'Right ankle/foot',
    left_ankle_foot: 'Left ankle/foot',
    other: 'Other',
}

const PROBLEM_TYPE_LABELS = {
    pain: 'Pain',
    stiffness: 'Stiffness',
    spasm: 'Spasm',
    numbness: 'Numbness',
    tingling: 'Tingling',
    weakness: 'Weakness',
    swelling: 'Swelling',
    instability: 'Instability',
    limited_motion: 'Limited motion',
    other: 'Other',
}

const TREATMENT_TYPE_LABELS = {
    emergency_care: 'Emergency care',
    primary_care: 'Primary care',
    orthopedic_care: 'Orthopedic care',
    neurosurgical_care: 'Neurosurgical care',
    neurology: 'Neurology',
    pain_management: 'Pain management',
    physical_therapy: 'Physical therapy',
    chiropractic: 'Chiropractic',
    acupuncture: 'Acupuncture',
    massage_therapy: 'Massage therapy',
    medications: 'Medications',
    injections: 'Injections',
    surgery: 'Surgery',
    other: 'Other',
}

const TEST_TYPE_LABELS = {
    xray: 'X-ray',
    mri: 'MRI',
    ct: 'CT',
    emg_ncs: 'EMG/NCS',
    ultrasound: 'Ultrasound',
    other: 'Other',
}

const TRI_STATE_LABELS = {
    yes: 'Yes',
    no: 'No',
    unsure: 'Unknown',
    not_applicable: 'N/A',
}

function labelFrom(map, value, fallback = 'Not asked') {
    if (value === undefined || value === null || value === '') {
        return fallback
    }
    return map[value] || String(value)
}

function formatCompletedAt(isoDate) {
    const date = isoDate ? new Date(isoDate) : new Date()
    if (Number.isNaN(date.getTime())) {
        return String(isoDate)
    }
    return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    })
}

export function buildIntakeUploadFileName(payload = {}) {
    const prefix = payload.meta?.type === 'general' ? 'general_patient_intake' : 'accident_patient_intake'
    const iso = payload.meta?.submittedAt || new Date().toISOString()
    const date = String(iso).slice(0, 10)

    return `${prefix}-${date}.pdf`
}

function getIntakeSummaryTitle(payload = {}) {
    return payload.meta?.type === 'general' ? 'General Intake Summary' : 'Accident Intake Summary'
}

function hasValue(value) {
    if (value === undefined || value === null || value === '') {
        return false
    }
    if (Array.isArray(value)) {
        return value.length > 0
    }
    if (typeof value === 'object') {
        return Object.values(value).some(hasValue)
    }
    return true
}

function buildSummaryModel(payload = {}) {
    const about = payload.aboutYou || {}
    const accident = payload.whatHappened || {}
    const felt = payload.howYouFelt || {}
    const symptoms = payload.symptomsToday || {}
    const screening = payload.healthScreening || {}
    const treatment = payload.treatmentReceived || {}
    const providers = payload.treatingProviders || {}
    const tests = payload.testsImaging || {}
    const previous = payload.previousInjuries || {}
    const medical = payload.medicalSurgical || {}
    const meds = payload.medicationsAllergies || {}
    const work = payload.workInformation || {}
    const social = payload.socialFamily || {}
    const activities = payload.dailyActivities || {}
    const healthQuestions = payload.healthQuestions || {}
    const review = payload.reviewSubmit || {}
    const confirm = payload.confirmSubmit || {}

    const patientName = about.fullName || 'Patient'
    const sections = []

    sections.push({
        title: 'Chief Complaint',
        lines: [review.chiefComplaint || '(not specified)'],
    })

    sections.push({
        title: 'Patient',
        lines: [
            `Name: ${about.fullName || 'Not asked'}`,
            `DOB: ${about.dateOfBirth || 'Not asked'}`,
            `Phone: ${about.phone || 'Not asked'}`,
            `Email: ${about.email || 'Not asked'}`,
        ],
    })

    if (payload.whatHappened) {
        const accidentDate = accident.incidentDate
            ? `${accident.incidentDate}${
                  accident.dateCertainty ? ` (${labelFrom(DATE_CERTAINTY_LABELS, accident.dateCertainty, '')})` : ''
              }`
            : 'Not asked'
        sections.push({
            title: 'Accident / Index Event',
            lines: [
                `Type: ${labelFrom(INCIDENT_TYPE_LABELS, accident.incidentType)}`,
                `Date: ${accidentDate}`,
                `Narrative: ${accident.description || 'Not asked'}`,
            ],
        })
    }

    const immediate = felt.immediateSymptoms || []
    const delayed = felt.delayedSymptoms || []
    if (immediate.length || delayed.length || felt.emsCalled || felt.erEvaluated) {
        const lines = []
        if (immediate.length) {
            lines.push('Immediate symptoms:')
            immediate.forEach((item, index) => {
                const region = labelFrom(BODY_REGION_LABELS, item.bodyRegion, '')
                lines.push(`  ${index + 1}. ${item.symptom || 'Symptom'}${region ? ` (${region})` : ''}`)
            })
        }
        if (delayed.length) {
            lines.push('Delayed symptoms:')
            delayed.forEach((item, index) => {
                const region = labelFrom(BODY_REGION_LABELS, item.bodyRegion, '')
                lines.push(`  ${index + 1}. ${item.symptom || 'Symptom'}${region ? ` (${region})` : ''}`)
            })
        }
        if (felt.emsCalled) {
            lines.push(`EMS called: ${labelFrom(TRI_STATE_LABELS, felt.emsCalled)}`)
        }
        if (felt.erEvaluated) {
            lines.push(`ER/urgent care: ${labelFrom(TRI_STATE_LABELS, felt.erEvaluated)}`)
        }
        sections.push({ title: 'How You Felt Afterward', lines })
    }

    const problems = symptoms.problems || []
    if (problems.length) {
        sections.push({
            title: payload.preventiveCare ? 'Reason for Visit' : 'Current Symptoms',
            lines: problems.map((problem, index) => {
                const region = labelFrom(BODY_REGION_LABELS, problem.bodyRegion, `Problem ${index + 1}`)
                const types = (problem.problemTypes || []).map((type) => PROBLEM_TYPE_LABELS[type] || type).join(', ')
                return types ? `${region} — ${types}` : region
            }),
        })
    }

    const symptomDetails = payload.symptomDetails
    if (symptomDetails) {
        const detailLines = []
        ;(symptomDetails.complaints || []).forEach((complaint, index) => {
            const region = labelFrom(BODY_REGION_LABELS, complaint.bodyRegion, `Complaint ${index + 1}`)
            const types = (complaint.problemTypes || []).map((type) => PROBLEM_TYPE_LABELS[type] || type).join(', ')
            detailLines.push(types ? `${region} — ${types}` : region)
            if (complaint.severity) {
                detailLines.push(`  Severity: ${complaint.severity}`)
            }
            if ((complaint.timing || []).length) {
                detailLines.push(`  Timing: ${complaint.timing.join(', ').replace(/_/g, ' ')}`)
            }
            if ((complaint.worse || []).length) {
                detailLines.push(`  Worse: ${complaint.worse.join(', ').replace(/_/g, ' ')}`)
            }
        })
        if ((symptomDetails.neurologicSymptoms || []).length) {
            detailLines.push(`Neurologic: ${symptomDetails.neurologicSymptoms.join(', ').replace(/_/g, ' ')}`)
        }
        if (detailLines.length) {
            sections.push({ title: 'Symptom Details', lines: detailLines })
        }
    }

    const screeningFields = [
        ['bladderControlLoss', 'New loss of bladder control'],
        ['bowelControlLoss', 'New loss of bowel control'],
        ['groinNumbness', 'Groin/inner thigh numbness'],
        ['worseningWeakness', 'Rapidly worsening weakness'],
        ['difficultyWalking', 'Severe new difficulty walking'],
    ]
    const answeredScreening = screeningFields.filter(([key]) => hasValue(screening[key]))
    if (answeredScreening.length) {
        sections.push({
            title: 'Health Screening',
            lines: answeredScreening.map(([key, label]) => `${label}: ${labelFrom(TRI_STATE_LABELS, screening[key])}`),
        })
    }

    const treatments = treatment.treatments || []
    if (treatments.length) {
        sections.push({
            title: 'Treatment Received',
            lines: treatments.map((item, index) => {
                const type = labelFrom(TREATMENT_TYPE_LABELS, item.treatmentType, `Treatment ${index + 1}`)
                return [type, item.providerName, item.facility].filter(Boolean).join(' · ')
            }),
        })
    }

    const providerList = providers.providers || []
    if (providerList.length) {
        sections.push({
            title: 'Treating Providers',
            lines: providerList.map((item, index) => item.providerName || `Provider ${index + 1}`),
        })
    }

    const testList = tests.tests || []
    if (testList.length) {
        sections.push({
            title: 'Tests & Imaging',
            lines: testList.map((item, index) => {
                const type = labelFrom(TEST_TYPE_LABELS, item.testType, `Test ${index + 1}`)
                const region = labelFrom(BODY_REGION_LABELS, item.bodyRegion, '')
                return [type, region, item.date].filter(Boolean).join(' · ')
            }),
        })
    }

    const priorInjuries = previous.priorInjuries || []
    if (priorInjuries.length) {
        sections.push({
            title: 'Previous Injuries',
            lines: priorInjuries.map((item, index) => {
                const type = labelFrom(INCIDENT_TYPE_LABELS, item.incidentType, `Prior injury ${index + 1}`)
                return [type, item.approximateDate, item.description].filter(Boolean).join(' · ')
            }),
        })
    }

    sections.push({
        title: 'Past Medical History',
        lines: [`Status: ${labelFrom(MEDICAL_CONDITION_LABELS, medical.medicalConditionsStatus)}`],
    })

    const surgeries = medical.surgeries || []
    if (surgeries.length) {
        sections.push({
            title: 'Past Surgical History',
            lines: surgeries.map((item, index) => {
                return [item.procedure || `Surgery ${index + 1}`, item.bodyRegion, item.dateOrYear].filter(Boolean).join(' · ')
            }),
        })
    }

    sections.push({
        title: 'Medications',
        lines: [`Status: ${labelFrom(MEDICATION_LABELS, meds.currentMedications)}`],
    })

    sections.push({
        title: 'Allergies',
        lines: [`Status: ${labelFrom(ALLERGY_LABELS, meds.allergies)}`],
    })

    if (hasValue(work.employmentStatus)) {
        sections.push({
            title: 'Work Information',
            lines: [`Employment status: ${String(work.employmentStatus).replace(/_/g, ' ')}`],
        })
    }

    sections.push({
        title: 'Social History',
        lines: [
            `Smoking: ${labelFrom(SMOKING_LABELS, social.smoking, 'Unknown')}`,
            `Alcohol: ${labelFrom(ALCOHOL_LABELS, social.alcohol, 'Unknown')}`,
            `Recreational drug use: ${labelFrom(DRUG_USE_LABELS, social.drugUse, 'Unknown')}`,
        ],
    })

    sections.push({
        title: 'Family History',
        lines: [`Status: ${labelFrom(FAMILY_HISTORY_LABELS, social.familyHistory)}`],
    })

    const assistiveDevices = activities.assistiveDevices || []
    const activityKeys = [
        'walking',
        'standing',
        'sitting',
        'bending',
        'lifting',
        'climbing_stairs',
        'driving',
        'sleeping',
        'dressing',
        'bathing',
        'household_chores',
        'shopping',
        'exercise',
        'work',
        'childcare_caregiving',
        'social_recreational',
    ]
    const limitedActivities = activityKeys.filter((key) => {
        const value = activities[key]
        return value && value !== 'no_difficulty' && value !== 'not_applicable'
    })
    if (limitedActivities.length || assistiveDevices.length) {
        const lines = []
        if (limitedActivities.length) {
            lines.push(`${limitedActivities.length} activity limitation(s) reported`)
            limitedActivities.slice(0, 8).forEach((key) => {
                lines.push(`  ${key.replace(/_/g, ' ')}: ${String(activities[key]).replace(/_/g, ' ')}`)
            })
        }
        if (assistiveDevices.length) {
            lines.push(
                `Assistive devices: ${assistiveDevices
                    .map((item) => item.device || 'device')
                    .join(', ')
                    .replace(/_/g, ' ')}`,
            )
        }
        sections.push({ title: 'Daily Activities', lines })
    }

    const preventive = payload.preventiveCare
    if (preventive) {
        const lines = [
            preventive.lastPhysicalExam ? `Last physical exam: ${preventive.lastPhysicalExam}` : null,
            preventive.immunizationsUpToDate ? `Immunizations up to date: ${preventive.immunizationsUpToDate}` : null,
            preventive.lastDentalVisit ? `Last dental visit: ${preventive.lastDentalVisit}` : null,
            preventive.lastEyeExam ? `Last eye exam: ${preventive.lastEyeExam}` : null,
            preventive.recentScreenings ? `Recent screenings: ${preventive.recentScreenings}` : null,
        ].filter(Boolean)

        if (lines.length) {
            sections.push({ title: 'Preventive Care', lines })
        }
    }

    const presentQuestions = Object.keys(healthQuestions).filter((key) => healthQuestions[key] === 'present')
    if (presentQuestions.length) {
        sections.push({
            title: 'Health Questions (Present)',
            lines: presentQuestions.map((key) => key.replace(/_/g, ' ')),
        })
    }

    if (confirm.signatureName) {
        sections.push({
            title: 'Confirmation',
            lines: [
                `Signed by: ${confirm.signatureName}`,
                `Confirmation accepted: ${confirm.confirmationAccepted ? 'Yes' : 'No'}`,
            ],
        })
    }

    return {
        patientName,
        completedAt: formatCompletedAt(payload.meta?.submittedAt),
        method: labelFrom(COMPLETION_METHOD_LABELS, about.completionMethod, 'Patient self'),
        reportedBy: labelFrom(INFORMATION_PROVIDER_LABELS, about.informationProvider, 'Patient'),
        sections,
    }
}

/**
 * Plain-text Accident Intake Summary (same content shown on success + clipboard).
 */
export function buildIntakeSummaryText(payload = {}) {
    const summary = buildSummaryModel(payload)
    const blocks = [
        `# ${getIntakeSummaryTitle(payload)} – ${summary.patientName}`,
        `Completed: ${summary.completedAt} · Method: ${summary.method} · Reported by: ${summary.reportedBy}`,
        '',
    ]

    summary.sections.forEach((section) => {
        blocks.push(`## ${section.title}`)
        ;(section.lines || []).forEach((line) => blocks.push(line))
        blocks.push('')
    })

    return blocks.join('\n').trim()
}

/**
 * Builds an intake PDF for the success-screen download and S3 upload.
 * `file` stays a data URI for the existing download link. `uploadFile` is the binary File for S3.
 */
export function buildIntakePdfFile(payload = {}) {
    const summary = buildSummaryModel(payload)
    const doc = new jsPDF({ unit: 'mm', format: 'letter' })
    const margin = 18
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const maxWidth = pageWidth - margin * 2
    let y = 20

    const ensureSpace = (needed = 8) => {
        if (y + needed > pageHeight - 16) {
            doc.addPage()
            y = 20
        }
    }

    const writeLines = (text, { fontSize = 11, fontStyle = 'normal', gap = 5.5, color = [33, 37, 41] } = {}) => {
        doc.setFont('helvetica', fontStyle)
        doc.setFontSize(fontSize)
        doc.setTextColor(...color)
        const lines = doc.splitTextToSize(String(text), maxWidth)
        lines.forEach((line) => {
            ensureSpace(gap + 2)
            doc.text(line, margin, y)
            y += gap
        })
    }

    writeLines(`${getIntakeSummaryTitle(payload)} – ${summary.patientName}`, {
        fontSize: 14,
        fontStyle: 'bold',
        gap: 7,
    })
    writeLines(`Completed: ${summary.completedAt} · Method: ${summary.method} · Reported by: ${summary.reportedBy}`, {
        fontSize: 10,
        gap: 6,
        color: [73, 80, 87],
    })
    y += 2

    summary.sections.forEach((section) => {
        ensureSpace(14)
        writeLines(section.title, {
            fontSize: 12,
            fontStyle: 'bold',
            gap: 6,
        })
        ;(section.lines || []).forEach((line) => {
            writeLines(line, { fontSize: 11, gap: 5.2 })
        })
        y += 3
    })

    const fileName = buildIntakeUploadFileName(payload)
    const blob = doc.output('blob')
    const uploadFile = new File([blob], fileName, { type: 'application/pdf' })

    return {
        fileName,
        file: doc.output('datauristring'),
        uploadFile,
        size: Math.max(1, Math.round(uploadFile.size / 1024)),
    }
}
